import { css } from "@linaria/core";
import cx from "classnames";
import * as Either from "fp-ts/Either";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import t from "../../../../../../../../../i18n";
import { Threshold, ThresholdComparison } from "diagnostics-domain/dashboard/type";
import { hashCode, omit as omitObject } from "diagnostics-lib/object";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Button from "@ui/Button";
import ButtonIconV2 from "@ui/ButtonIconV2";
import Checkbox from "@ui/Checkbox";
import { AddCircleLine, DeleteBinLine } from "@ui/IconV2/react";
import InputV2 from "@ui/Input/InputV2";
import Menu from "@ui/Menu";
import Select from "@ui/Select";
import Switch from "@ui/Switch";
import { colors, spacings } from "@ui/Theme";
import TooltipV2 from "@ui/TooltipV2";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import type { BlockDiagramModelT } from "../../../../../../schema/block/diagram";

const THRESHOLD_COLORS = [
  "#E26869",
  "#EFA9A9",
  "#F7D4D4", // Red
  "#FFA556",
  "#FFCC9F",
  "#FFE5CF", // Orange
  "#6BBD6B",
  "#ABD9AB",
  "#D5ECD5", // Green
  "#FFFFFF", // White
];

const THRESHOLD_COMPARISON: { value: ThresholdComparison; label: string }[] = [
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
];

export const AnnotationLayerThresholdsSettings = memo(
  ({
    block,
    annotation,
    encodingOptions,
  }: {
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
    encodingOptions: { label: string; value: string }[];
  }) => {
    const editor = useDashboardEditorContext();
    const enabled = useMemo(
      () => (annotation?.thresholds?.length ?? 0) > 0,
      [annotation?.thresholds?.length],
    );
    const options = useMemo(
      () =>
        encodingOptions.filter(o =>
          annotation?.encodings.some(e => {
            if (
              e.column === "quantityImpacts" ||
              e.column === "systemImpact" ||
              e.column === "externalImpacts" ||
              e.column === "unexplainedLosses"
            )
              return `${e.column}/${e.quantity}` === o.value;
            return e.column === "gap" && o.value === "normalizedGap" ? true : e.column === o.value;
          }),
        ),
      [annotation?.encodings, encodingOptions],
    );
    const defaultEncoding = useMemo(
      () =>
        options.find(option => option.value === "normalizedGap")?.value ?? options[0]?.value ?? "",
      [options],
    );

    const selectedEncoding = useMemo(() => {
      const threshold = annotation.thresholds[0];
      if (threshold && threshold.quantity) return `${threshold.column}/${threshold.quantity}`;
      return threshold?.column;
    }, [annotation.thresholds]);

    const [cachedThresholds, setCachedThresholds] = useState<Threshold[] | null>(null);

    useEffect(() => {
      if (enabled && (annotation?.thresholds?.length ?? 0) > 0) {
        setCachedThresholds(null);
      }
    }, [annotation?.thresholds, enabled]);

    const toggleThresholds = useCallback(() => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: block.config.layers.map(l => {
            if (l === annotation) {
              const [column, quantity] = defaultEncoding.split("/");

              if (enabled) {
                setCachedThresholds(l.thresholds);
                l.thresholds = [];
              } else {
                l.thresholds = cachedThresholds ?? [
                  column === "normalizedGap"
                    ? {
                        color: "#E47373",
                        value: 2,
                        column,
                        quantity: quantity ?? null,
                        comparison: "gte" as const,
                        absolute: true,
                      }
                    : {
                        color: "#FFFFFF",
                        value: null,
                        column: column ?? "",
                        quantity: quantity ?? null,
                        comparison: "gte" as const,
                        absolute: null,
                      },
                ];
              }
            }
            return l;
          }),
        },
      });
    }, [annotation, block, cachedThresholds, defaultEncoding, editor, enabled]);

    const updateEncoding = useCallback(
      (encoding: string) => {
        if (!block) return;
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(l => {
              if (l === annotation) {
                const [column, quantity] = encoding.split("/");
                if (!column) return l;
                l.thresholds = l.thresholds.map(t => ({
                  ...t,
                  column,
                  quantity: quantity ?? null,
                  absolute: column === "gap" || column === "normalizedGap" ? t.absolute : null,
                }));
              }
              return l;
            }),
          },
        });
      },
      [annotation, block, editor],
    );

    const allowAbsoluteValue = useMemo(
      () => selectedEncoding === "gap" || selectedEncoding === "normalizedGap",
      [selectedEncoding],
    );

    const updateThresholdsAbsoluteComparison = useCallback(
      (absolute: boolean) => {
        if (!block) return;
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(l => {
              if (l === annotation) {
                l.thresholds = l.thresholds.map(t => ({
                  ...t,
                  absolute,
                }));
              }
              return l;
            }),
          },
        });
      },
      [annotation, block, editor],
    );

    return (
      <div className={annotationThresholdSettings}>
        <Switch position="right" justify checked={enabled} onChange={toggleThresholds}>
          {t("project.dashboard.editor.gui.section.rule.title")}
        </Switch>
        {enabled && (
          <>
            <div className={encodingSelection}>
              <Select
                label={t(
                  "project.dashboard.editor.gui.section.annotation_settings.assigned_threshold",
                )}
                options={options}
                revertable={false}
                value={selectedEncoding}
                onChange={e => updateEncoding(e.target.value)}
                prompt=""
                clearable={false}
              />
              <Checkbox
                disabled={!allowAbsoluteValue}
                checked={annotation.thresholds.every(t => t.absolute)}
                onChange={e => updateThresholdsAbsoluteComparison(e.target.checked)}
                overlay={
                  allowAbsoluteValue
                    ? t(
                        "project.dashboard.editor.gui.section.annotation_settings.absolute_threshold",
                      )
                    : t(
                        "project.dashboard.editor.gui.section.annotation_settings.disabled_absolute_threshold",
                      )
                }
              >
                Abs.
              </Checkbox>
            </div>
            {annotation?.thresholds?.map((threshold, i) => (
              <AnnotationThresholdSetting
                key={hashCode({ i, ...omitObject(threshold, ["value"]) })}
                block={block}
                annotation={annotation}
                threshold={threshold}
              />
            ))}
            <AddAnnotationThresholdButton block={block} annotation={annotation} />
            {editor?.error &&
              editor?.errors?.some(
                err => err.context[err.context.length - 1]?.key === "thresholds",
              ) && <AdditionnalMessage status="ERROR" text={editor.error} />}
          </>
        )}
      </div>
    );
  },
);

const AnnotationThresholdSetting = memo(
  ({
    block,
    annotation,
    threshold,
  }: {
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
    threshold: BlockDiagramModelT["config"]["layers"][number]["thresholds"][number];
  }) => {
    const editor = useDashboardEditorContext();
    const [value, setValue] = useState<number | undefined>(threshold.value ?? undefined);

    const updateThreshold = useCallback(
      <K extends keyof Threshold, V extends Threshold[K]>(key: K, value: V) => {
        if (!block) return;

        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(l => {
              if (l !== annotation) return l;
              l.thresholds = l.thresholds.map(t => {
                if (t !== threshold) return t;
                t[key] = value;
                return t;
              });
              return l;
            }),
          },
        });
      },
      [annotation, block, editor, threshold],
    );

    const removeThreshold = useCallback(() => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: block.config.layers.map(l => {
            if (l === annotation) {
              l.thresholds = l.thresholds.filter(t => t !== threshold);
            }
            return l;
          }),
        },
      });
    }, [annotation, block, editor, threshold]);

    return (
      <div className={annotationThresholdSetting}>
        <Select
          options={THRESHOLD_COMPARISON}
          value={threshold.comparison}
          onChange={e => {
            if (Either.isLeft(ThresholdComparison.decode(e.target.value))) return;
            updateThreshold("comparison", e.target.value as ThresholdComparison);
          }}
          prompt=""
          clearable={false}
        />
        <InputV2
          value={value}
          type="number"
          onChange={e => {
            const value = e.target.value.length > 0 ? Number(e.target.value) : undefined;
            setValue(value);
            if (value) updateThreshold("value", value);
          }}
          placeholder={t(
            "project.dashboard.editor.gui.section.annotation_settings.threshold_placeholder",
          )}
        />
        <Menu
          matchChildWidth
          closeOnOutsideClick
          overlay={
            <>
              {THRESHOLD_COLORS.map(c => (
                <Button
                  key={c}
                  className={cx(colorPicker, "close-on-click")}
                  onClick={() => updateThreshold("color", c)}
                >
                  <ColorSquare color={c} />
                </Button>
              ))}
            </>
          }
        >
          <Button variant="tertiary">
            <ColorSquare color={threshold.color} />
          </Button>
        </Menu>
        <TooltipV2 overlay={t("commons.button.deactivate")}>
          <div>
            <ButtonIconV2 onClick={removeThreshold}>
              <DeleteBinLine />
            </ButtonIconV2>
          </div>
        </TooltipV2>
      </div>
    );
  },
);

function ColorSquare_({ color }: { color: string }) {
  return <div style={{ backgroundColor: color }} className={colorSquare} />;
}
const ColorSquare = memo(ColorSquare_);

function AddAnnotationThresholdButton({
  block,
  annotation,
}: {
  block: BlockDiagramModelT;
  annotation: BlockDiagramModelT["config"]["layers"][number];
}) {
  const editor = useDashboardEditorContext();

  const addAnnotationThreshold = useCallback(() => {
    if (!block) return;

    void editor?.updateBlock({
      ...block,
      config: {
        ...block.config,
        layers: block.config.layers.map(l => {
          if (l === annotation) {
            return {
              ...l,
              thresholds: [
                ...(l.thresholds ?? []),
                {
                  column: l.thresholds[0]?.column ?? "",
                  quantity: l.thresholds[0]?.quantity ?? null,
                  color: "#FFFFFF",
                  value: null,
                  comparison: "gte" as const,
                  absolute: l.thresholds.length > 0 ? l.thresholds.some(t => t.absolute) : null,
                },
              ],
            };
          }
          return l;
        }),
      },
    });
  }, [annotation, block, editor]);

  return (
    <Button
      variant="tertiary"
      slim
      leftIcon={<AddCircleLine />}
      disabled={!block?.config.url}
      onClick={addAnnotationThreshold}
    >
      {t("project.dashboard.editor.gui.section.data.add_button")}
    </Button>
  );
}

const annotationThresholdSettings = css`
  margin-top: ${spacings.m};

  > * + * {
    margin-top: ${spacings.xs};
  }

  &,
  > * {
    width: 100%;
  }
`;

const annotationThresholdSetting = css`
  display: grid;
  grid-template-columns: 1fr 1fr max-content 28px;
  align-items: center;
  column-gap: ${spacings.xs};
`;

const colorSquare = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${colors.titanium200};
`;

const colorPicker = css`
  &:hover {
    background-color: ${colors.titanium100};
  }
`;

const encodingSelection = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};

  > :first-child {
    flex-grow: 1;
  }

  > :last-child {
    margin-top: 18px;
    width: max-content;
  }
`;
